<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-container>
      <v-row align="center" justify="start">
        <v-col cols="12">
          <v-card class="mx-auto" variant="plain" elevation="5" title="Welcome to EpiX">
            <v-card-text>
              <p>
                EpiX is an ecosystem for all your RWE/RWD needs. Please select among the following functions.
              </p>
            </v-card-text>
            <div v-if="!isAuthenticated">
              <v-card-text>
                <strong>
                  You are not authenticated.
                </strong>
              </v-card-text>
              <v-btn @click="reLogIn">Log In</v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6" v-if="hasAnyAccessRight(['patient-level-data', 'view-disabled-functions'])">
          <v-card class="ct-background" min-height="700" max-height="700" variant="plain" elevation="5"
                  title="Patient-level Data" style="overflow-y: scroll">
            <template v-slot:prepend>
              <v-icon icon="mdi-account-multiple-outline" color="primary"></v-icon>
            </template>
            <v-list>
              <div v-if="hasAccessRight('patient-level-data')">
                <v-list-item v-if="patient_level_data_items.filter(item => icd10CodeAuthorized(item.icd10)).length > 0">
                  You currently have access to the following datasets.
                </v-list-item>
                <v-list-item v-for="item in patient_level_data_items.filter(item => icd10CodeAuthorized(item.icd10))"
                             :key="item.title"
                             :value="item.title"
                             :title="item.title"
                             @click="$router.push(item.route)">
                </v-list-item>
              </div>
              <div v-else>
                <v-list-item>
                  You currently do not have access to any of these datasets.
                </v-list-item>
              </div>
              <v-divider :vertical="true"></v-divider>
              <v-list-item>
                Swedish National Registers offer unique population-based and nation-wide patient-level data. In EpiX
                we have created disease specific cohorts based on anonymized patient level data from these registers to
                enable data exploration to generate on-demand insights. Data from the following registers has been
                linked and can include some examples of the following information:
              </v-list-item>
              <v-list-item prepend-icon="mdi:mdi-circle-small">
                The Cancer Register (diagnosis from ICD codes, date of diagnosis, sex, age at diagnosis)
              </v-list-item>
              <v-list-item prepend-icon="mdi:mdi-circle-small">
                The Patient Register (admission/discharge, selected procedures performed in inpatient and specialized
                outpatient care, ATC codes)
              </v-list-item>
              <v-list-item prepend-icon="mdi:mdi-circle-small">
                The Prescribed Drug Register (drugs identified with ATC codes dispensed by pharmacies including drug
                class, dispensation date etc.)
              </v-list-item>
              <v-list-item prepend-icon="mdi:mdi-circle-small">
                The Cause of Death Register (specific cause of death, date of death)
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
        <v-col cols="6" v-if="hasAnyAccessRight(['socialstyrelsen-generic', 'view-disabled-functions'])">
          <v-card class="ct-background" min-height="700" max-height="700" variant="plain" elevation="5"
                  title="Regional patient data"
                  subtitle="Number of patients, hospitalizations, visits, and causes of death in Sweden."
                  style="overflow-y: scroll">
            <template v-slot:prepend>
              <v-icon icon="mdi-map" color="primary"></v-icon>
            </template>
            <template v-slot:append>
              <v-responsive width="200px">
                <v-text-field v-model="regionalDataItemsSearch"
                              @update:model-value="regionalDataItemsUpdated"
                              label="Search"
                              placeholder="Search"
                              :disabled="regionalDataItemsUnfiltered.size === 0"
                              hide-details filled dense></v-text-field>
              </v-responsive>
            </template>
            <v-list min-height="600" max-height="600" v-model:opened="openRegionalDataMenuItems">
              <v-list-item-title class="ml-5" v-if="regionalDataItemsUnfiltered.size > 0">
                The following datasets are available.
              </v-list-item-title>
              <v-list-item-title class="ml-5" v-else>
                You currently do not have access to any of these datasets.
              </v-list-item-title>
              <v-divider :vertical="true"></v-divider>
              <RecursivePatientDataMenu :items="regionalDataItems"/>
            </v-list>
          </v-card>
        </v-col>
        <v-col cols="6" v-if="hasAnyAccessRight(['socialstyrelsen-generic', 'view-disabled-functions'])">
          <v-card class="ct-background" min-height="700" max-height="700" variant="plain" elevation="5"
                  title="Regional drug dispensation" subtitle="Number of patients per ATC chapter/code in Sweden."
                  style="overflow-y: scroll">
            <template v-slot:prepend>
              <v-icon icon="mdi-map" color="primary"></v-icon>
            </template>
            <template v-slot:append>
              <v-responsive width="200px">
                <v-text-field v-model="regionalDrugDataItemsSearch"
                              @update:model-value="regionalDrugDataItemsUpdated"
                              label="Search"
                              placeholder="Search"
                              :disabled="regionalDrugDataItemsUnfiltered.size === 0"
                              hide-details filled dense></v-text-field>
              </v-responsive>
            </template>
            <v-list min-height="600" max-height="600" v-model:opened="openRegionalDrugDataMenuItems">
              <v-list-item-title class="ml-5" v-if="regionalDrugDataItemsUnfiltered.size > 0">
                The following datasets are available.
              </v-list-item-title>
              <v-list-item-title class="ml-5" v-else>
                You currently do not have access to any of these datasets.
              </v-list-item-title>
              <v-divider :vertical="true"></v-divider>
              <RecursiveDrugsMenu :items="regionalDrugDataItems"/>
            </v-list>
          </v-card>
        </v-col>
        <v-col cols="6" v-if="hasAnyAccessRight(['clinical-trials', 'view-disabled-functions'])">
          <v-card class="ct-background" min-height="700" max-height="700" variant="plain" elevation="5"
                  title="Clinical trials" subtitle="Clinical trials from ClinicalTrials.gov by MeSH descriptor."
                  style="overflow-y: scroll">
            <template v-slot:prepend>
              <v-icon icon="mdi-pill" color="primary"></v-icon>
            </template>
            <template v-slot:append>
              <v-responsive width="200px">
                <v-text-field v-model="clinicalTrialsItemsSearch"
                              @update:model-value="clinicalTrialsItemsUpdated"
                              label="Search"
                              placeholder="Search"
                              :disabled="clinicalTrialsItemsUnfiltered.size === 0"
                              hide-details filled dense></v-text-field>
              </v-responsive>
            </template>
            <v-list min-height="600" max-height="600" v-model:opened="openClinicalTrialsMenuItems">
              <v-list-item-title class="ml-5" v-if="clinicalTrialsItemsUnfiltered.size > 0">
                The following datasets are available.
              </v-list-item-title>
              <v-list-item-title class="ml-5" v-else>
                You currently do not have access to any of these datasets.
              </v-list-item-title>
              <v-divider :vertical="true"></v-divider>
              <v-list-item title="All clinical trials"
                           @click="$router.push(({name: 'AllClinicalTrials', params: {}}))"
                           v-if="clinicalTrialsItemsUnfiltered.size > 0">
              </v-list-item>
              <RecursiveClinicalTrialsMenu :items="clinicalTrialsItems"/>
            </v-list>
          </v-card>
        </v-col>
        <v-col cols="6" v-if="hasAnyAccessRight(['data-landscaping', 'view-disabled-functions'])">
          <v-card class="ct-background" min-height="400" max-height="400" variant="plain" elevation="5"
                  title="RWD Landscaping" style="overflow-y: scroll">
            <template v-slot:prepend>
              <v-icon icon="mdi-database" color="primary"></v-icon>
            </template>
            <v-list>
              <v-list-item>
                A comprehensive catalog of your Real-World Data sources and key clinics/potential partners.
              </v-list-item>
              <v-list-item prepend-icon="mdi-database" @click="$router.push({name: 'LandscapingDataSources'})"
                           :disabled="!hasAccessRight('data-landscaping')">
                Data Sources
              </v-list-item>
              <v-list-item prepend-icon="mdi-hospital-building" @click="$router.push({name: 'LandscapingClinics'})"
                           :disabled="!hasAccessRight('data-landscaping')">
                Key Clinics/Potential Partners
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
        <v-col cols="6" v-if="hasAnyAccessRight(['custom-studies', 'view-disabled-functions'])">
          <v-card class="ct-background" min-height="400" max-height="400" variant="plain" elevation="5"
                  title="Custom Studies Catalog" style="overflow-y: scroll">
            <template v-slot:prepend>
              <v-icon icon="mdi-file-document" color="primary"></v-icon>
            </template>
            <v-list>
              <v-list-item>
                A comprehensive catalog of your custom Real-World Evidence studies.
              </v-list-item>
              <v-list-item prepend-icon="mdi-file-document" @click="$router.push({name: 'CustomStudies'})"
                           :disabled="!hasAccessRight('custom-studies')">
                Custom Studies Catalog
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
        <v-col cols="6" v-if="hasAnyAccessRight(['virtual-panel', 'virtual-panel-admin', 'view-disabled-functions'])">
          <v-card class="ct-background" min-height="400" max-height="400" variant="plain" elevation="5"
                  title="Virtual Expert Panels" style="overflow-y: scroll">
            <template v-slot:prepend>
              <v-icon icon="mdi-file-document" color="primary"></v-icon>
            </template>
            <v-list>
              <v-list-item>
                Virtual expert panels/advisory boards.
              </v-list-item>
              <v-list-item prepend-icon="mdi-file-document" @click="$router.push({name: 'VirtualPanels'})"
                           :disabled="!hasAnyAccessRight(['virtual-panel', 'virtual-panel-admin'])">
                Virtual Panels
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import axios from "axios";
import RecursiveDrugsMenu from "@/components/RecursiveDrugsMenu.vue";
import RecursivePatientDataMenu from "@/components/RecursivePatientDataMenu.vue";
import RecursiveClinicalTrialsMenu from "@/components/RecursiveClinicalTrialsMenu.vue";

function debounce(func, timer, timeout = 1000) {
  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    return setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

function createMap(data) {
  let map = new Map();
  for (let key in data) {
    let value = Object.assign({}, data[key]);
    value.children = createMap(value.children);
    map.set(key, value);
  }
  return map
}

function filterMap(map, filter, keys) {
  let newMap = new Map();
  let newKeys = [];
  for (let [key, value] of map) {
    if (filter(value)) {
      newKeys.push(value.code);
      newMap.set(key, structuredClone(value));
      continue;
    }
    if (value.children) {
      let filteredChildren = filterMap(value.children, filter, keys);
      let newChildren = filteredChildren[0];
      if (newChildren.size > 0) {
        newKeys.push(value.code);
        newKeys.push(...filteredChildren[1])
        let copy = Object.assign({}, value);
        copy.children = newChildren;
        newMap.set(key, copy);
      }
    }
  }
  return [newMap, keys.concat(newKeys)];
}

export default defineComponent({
  name: 'DashboardView',
  components: {RecursivePatientDataMenu, RecursiveDrugsMenu, RecursiveClinicalTrialsMenu},
  computed: {
    isAuthenticated() {
      return this.$store.getters.isAuthenticated;
    }
  },
  methods: {
    async reLogIn() {
      await this.$store.dispatch('logOut').then(() => this.$store.dispatch('logIn'))
    },
    icd10CodeAuthorized(icd10Code) {
      return this.$store.getters.stateUser?.icd10_codes?.includes(icd10Code);
    },
    hasAnyAccessRight(accessRights) {
      return this.$store.getters.stateUser?.access_rights?.some(accessRight => accessRights.includes(accessRight));
    },
    hasAccessRight(accessRight) {
      return this.$store.getters.stateUser?.access_rights?.includes(accessRight);
    },
    async loadRegionalDataItems() {
      if (!this.hasAccessRight('socialstyrelsen-generic')) {
        return;
      }
      return axios.get('/socialstyrelsen-generic/diagnoses-tree', {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
        params: {}
      });
    },
    regionalDataItemsUpdated() {
      this.regionalDataItemsSearchTimer = debounce(() => {
        if (this.regionalDataItemsSearch === '') {
          this.regionalDataItems = structuredClone(this.regionalDataItemsUnfiltered);
          return;
        }
        let keys = [];
        let filteredMap = filterMap(this.regionalDataItemsUnfiltered, item =>
            item.diagnosis.toLowerCase().includes(this.regionalDataItemsSearch.toLowerCase()) ||
            item.code.toLowerCase().includes(this.regionalDataItemsSearch.toLowerCase()), keys)
        this.regionalDataItems = filteredMap[0];
        this.openRegionalDataMenuItems = filteredMap[1];
      }, this.regionalDataItemsSearchTimer)()
    },
    async loadRegionalDrugDataItems() {
      if (!this.hasAccessRight('socialstyrelsen-generic')) {
        return;
      }
      return axios.get('/socialstyrelsen-generic/atc-tree', {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
        params: {}
      });
    },
    regionalDrugDataItemsUpdated() {
      this.regionalDrugDataItemsSearchTimer = debounce(() => {
        if (this.regionalDrugDataItemsSearch === '') {
          this.regionalDrugDataItems = structuredClone(this.regionalDrugDataItemsUnfiltered);
          return;
        }
        let keys = [];
        let filteredMap = filterMap(this.regionalDrugDataItemsUnfiltered, item =>
            item.description.toLowerCase().includes(this.regionalDrugDataItemsSearch.toLowerCase()) ||
            item.code.toLowerCase().includes(this.regionalDrugDataItemsSearch.toLowerCase()), keys)
        this.regionalDrugDataItems = filteredMap[0];
        this.openRegionalDrugDataMenuItems = filteredMap[1];
      }, this.regionalDrugDataItemsSearchTimer)()
    },
    async loadClinicalTrials() {
      if (!this.hasAccessRight('clinical-trials')) {
        return;
      }
      return axios.get('/mesh-codes',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
            }
          });
    },
    clinicalTrialsItemsUpdated() {
      this.clinicalTrialsItemsSearchTimer = debounce(() => {
        if (this.clinicalTrialsItemsSearch === '') {
          this.clinicalTrialsItems = structuredClone(this.clinicalTrialsItemsUnfiltered);
          return;
        }
        let keys = [];
        let filteredMap = filterMap(this.clinicalTrialsItemsUnfiltered, item =>
            item.description.toLowerCase().includes(this.clinicalTrialsItemsSearch.toLowerCase()) ||
            item.code.toLowerCase().includes(this.clinicalTrialsItemsSearch.toLowerCase()), keys)
        this.clinicalTrialsItems = filteredMap[0];
        this.openClinicalTrialsMenuItems = filteredMap[1];
      }, this.clinicalTrialsItemsSearchTimer)()
    }
  },
  data: () => ({
    patient_level_data_items: [
      {
        title: 'C18, C19, or C20: De novo metastatic colorectal cancer',
        icd10: 'C18_C19_C20',
        route: ({name: 'C18_C19_C20'})
      },
      {
        title: 'C34: Malignant neoplasm of bronchus and lung',
        icd10: 'C34',
        route: ({name: 'C34'})
      },
      {
        title: 'C34: Malignant neoplasm of bronchus and lung (sample)',
        icd10: 'C34sample',
        route: ({name: 'C34sample'})
      },
      {
        title: 'C90.0: Multiple myeloma',
        icd10: 'C90.0',
        route: ({name: 'C90.0'})
      },
      {
        title: 'C90.0: Multiple myeloma (sample)',
        icd10: 'C90.0sample',
        route: ({name: 'C90.0sample'})
      },
      {
        title: 'E75.2F: Fabry disease',
        icd10: 'E75.2F',
        route: ({name: 'E75.2F'})
      },
      {
        title: 'G70.0: Myasthenia gravis',
        icd10: 'G70.0',
        route: ({name: 'G70.0'})
      }
    ],
    regionalDataItemsSearch: '',
    regionalDataItemsSearchTimer: undefined,
    regionalDataItems: new Map(),
    regionalDataItemsUnfiltered: new Map(),
    openRegionalDataMenuItems: [],
    regionalDrugDataItemsSearch: '',
    regionalDrugDataItemsSearchTimer: undefined,
    regionalDrugDataItems: new Map(),
    regionalDrugDataItemsUnfiltered: new Map(),
    openRegionalDrugDataMenuItems: [],
    clinicalTrialsItemsSearch: '',
    clinicalTrialsItemsSearchTimer: undefined,
    clinicalTrialsItems: new Map(),
    clinicalTrialsItemsUnfiltered: new Map(),
    openClinicalTrialsMenuItems: []
  }),
  async mounted() {
    await Promise.all([
      this.loadRegionalDataItems(),
      this.loadRegionalDrugDataItems(),
      this.loadClinicalTrials()
    ]).then(responses => {
      if (responses[0]) {
        this.regionalDataItemsUnfiltered = createMap(responses[0].data);
        this.regionalDataItems = structuredClone(this.regionalDataItemsUnfiltered);
      }
      if (responses[1]) {
        this.regionalDrugDataItemsUnfiltered = createMap(responses[1].data);
        this.regionalDrugDataItems = structuredClone(this.regionalDrugDataItemsUnfiltered);
      }
      if (responses[2]) {
        this.clinicalTrialsItemsUnfiltered = createMap(responses[2].data);
        this.clinicalTrialsItems = structuredClone(this.clinicalTrialsItemsUnfiltered);
      }
      console.log('Menu items loaded');
    })
  }
});
</script>

<style scoped>
.ct-background {
  background: url("../assets/ciencia-technology-logo-plain-transparent-no-padding.svg") no-repeat center center;
  background-size: 40% auto;
}

div {
  background: rgba(0, 0, 0, 0);
}
</style>
